import {
  AUDIENCE_AUTHORIZED_TYPE,
  AUDIENCE_UNAUTHORIZED_TYPE,
  DISTRIBUTION_BIRTHDAY_TYPE,
  audienceOptions,
  distributionTypeOptions,
  genderOptions,
} from 'constants/distribution';
import {
  DatePicker,
  Dropdown,
  Form,
  FormInstance,
  Input,
  Menu,
  Radio,
  Select,
  Slider,
  Tag,
  Typography,
  notification,
} from 'antd';
import { disabledPastDates, disabledPastTimes, roundedDate } from 'utils/date';
import { getDropdownStyles, getMenuStyles } from './styles';
import {
  loadOfferRecommendationsAction,
  loadRecommendationsAction,
} from 'store/recommendations/recommendations.actions';
import { useCallback, useEffect, useState } from 'react';

import { IDistributionData } from 'types/distribution.types';
import { IServiceItem } from 'types/recommendations.types';
import { citiesSelector } from 'store/dictionaries/dictionaries.selectors';
import dayjs from 'dayjs';
import { getFormErrorFields } from 'utils/errors';
import { useAppDispatch } from 'store';
import { useAppSelector } from 'store';
import useListData from 'hooks/useListData';

const MINUTE_STEP = 10;
const MIN_AGE = 18;
const MAX_AGE = 100;

type FormData = Omit<IDistributionData, 'age'> & { age: [number, number] };

export const DistributionForm = ({
  onFinish,
  initialValues,
  form,
  isEdit,
}: {
  onFinish: (data: IDistributionData) => Promise<any>;
  initialValues: Partial<IDistributionData>;
  form: FormInstance;
  isEdit?: boolean;
}) => {
  const { options: cities, loading: citiesLoading } = useAppSelector(citiesSelector);
  const radioField = Form.useWatch('audience', form);
  const type = Form.useWatch('type', form);
  const age = Form.useWatch('age', form);
  // const dateToSend = Form.useWatch('dateToSend', form);

  const dispatch = useAppDispatch();
  const handleListLoad = useCallback(
    () => dispatch(loadRecommendationsAction({ page: 1, pageSize: 1000 })),
    [dispatch],
  );
  const handleListLoad1 = useCallback(() => dispatch(loadOfferRecommendationsAction({ page: 1, pageSize: 1000 })), []);

  const { items: recommendations } = useListData(handleListLoad);
  const { items: actions } = useListData(handleListLoad1);
  const [selectedRec, setSelectedRec] = useState<any | null>(null);
  const [selectedAction, setSelectedAction] = useState<any | null>(null);

  const reset = () => {
    setSelectedRec(null);
    setSelectedAction(null);
  };

  const currentDate = dayjs();

  const activeActions = actions?.filter((act: any) => {
    const startDate = dayjs(act.workTimeInterval?.from);
    const endDate = act.workTimeInterval?.to ? dayjs(act.workTimeInterval?.to) : null;
    return currentDate.isAfter(startDate) && (!endDate || currentDate.isBefore(endDate));
  });

  const activeRecommendations = recommendations?.filter((rec: any) => {
    const startDate = dayjs(rec.workTimeInterval?.from);
    const endDate = rec.workTimeInterval?.to ? dayjs(rec.workTimeInterval?.to) : null;
    return currentDate.isAfter(startDate) && (!endDate || currentDate.isBefore(endDate));
  });

  const showItem = useCallback(
    (item: any) =>
      item?.title ? (
        <Typography.Text ellipsis style={{ maxWidth: '450px' }}>
          {item.title}
        </Typography.Text>
      ) : (
        <Tag style={{ margin: '0 2px 2px 0' }} key={item.id + '/' + item.city.id}>
          <Typography.Text ellipsis style={{ maxWidth: '450px' }}>
            {item.name} <b>({item.city.name})</b>
          </Typography.Text>
        </Tag>
      ),
    [],
  );

  const menuRec = (
    <Menu>
      {activeRecommendations.map((el: any) => (
        <Menu.Item key={el.id}>
          <div
            style={getMenuStyles()}
            onClick={() => {
              setSelectedRec(el);
              form.setFieldValue('action', el);
              form.validateFields().catch((error) => console.log(error));
            }}
          >
            {el?.items?.map((item: IServiceItem) => showItem(item))}
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  const menu = (
    <Menu>
      {activeActions.map((el: any) => (
        <Menu.Item key={el.id}>
          <div
            style={getMenuStyles()}
            onClick={() => {
              setSelectedAction(el);
              form.setFieldValue('action', el);
              form.validateFields().catch((error) => console.log(error));
            }}
          >
            {showItem(el)}
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  const handleFinish = async ({ age, gender, ...values }: FormData) => {
    try {
      const data = {
        ...values,
        age: age ? { from: age[0], to: age[1] } : null,
        gender: gender || null,
        externalId: selectedAction?.id || selectedRec?.id || null,
      };

      await onFinish(data);

      form.resetFields();
      reset();
    } catch (e: any) {
      if (e && e.violations) {
        const errorFields = getFormErrorFields(e.violations, {});
        form.setFields(errorFields);
      } else {
        notification.error(e);
      }
    }
  };

  useEffect(() => {
    // if (!isEdit) reset();
    reset();
  }, [type]);

  const resetUserData = useCallback(() => {
    form.setFieldValue('audience', AUDIENCE_UNAUTHORIZED_TYPE);
    form.setFieldValue('gender', undefined);
    form.setFieldValue('age', [18, 100]);
    form.setFieldValue('cityIds', undefined);
  }, [form]);

  useEffect(() => {
    // resetUserData();
    if (initialValues?.externalId && recommendations && actions && type) {
      if (type === distributionTypeOptions?.[2].value) {
        const rec = recommendations.find((el: any) => el.id === initialValues.externalId);
        if (rec) setSelectedRec(rec);
      }
      if (type === distributionTypeOptions?.[0].value) {
        const act = actions.find((el: any) => el.id === initialValues.externalId);
        if (act) setSelectedAction(act);
      }
    }
  }, [initialValues, recommendations, actions, type]);

  useEffect(
    () => () => {
      reset();
    },
    [],
  );

  useEffect(() => {
    const data = selectedAction || selectedRec;
    if (!data) return;
    resetUserData();

    const age = data?.age;
    const gender = data?.gender;
    const cityIds = data?.cityIds;
    if (age || gender) form.setFieldValue('audience', AUDIENCE_AUTHORIZED_TYPE);
    if (gender) form.setFieldValue('gender', gender);
    if (age) form.setFieldValue('age', [age.from, age.to]);
    if (cityIds) form.setFieldValue('cityIds', cityIds);
  }, [selectedAction, selectedRec]);

  return (
    <Form
      initialValues={{
        ...initialValues,
        cityId: initialValues.cityId || [],
        age: [initialValues.age?.from || MIN_AGE, initialValues.age?.to || MAX_AGE],
      }}
      onFinish={handleFinish}
      preserve={false}
      scrollToFirstError={true}
      layout="vertical"
      form={form}
      labelWrap
      colon={false}
      autoComplete="off"
    >
      <Form.Item name="type" label="Тип рассылки" rules={[{ required: true }]}>
        <Select disabled={!!initialValues?.type} placeholder="Выберите тип" options={distributionTypeOptions} />
      </Form.Item>

      {type === distributionTypeOptions?.[2].value && (
        <Form.Item
          label="Рекомендация"
          name="action"
          rules={[{ required: true, message: 'Пожалуйста, выберите рекомендацию' }]}
        >
          <Dropdown overlay={menuRec} trigger={['click']} disabled={!!isEdit}>
            <a onClick={(e) => e.preventDefault()} style={getDropdownStyles(!!isEdit)}>
              {selectedRec?.id
                ? selectedRec?.items?.map((item: IServiceItem) => showItem(item))
                : '--- Нажмите для выбора ---'}
            </a>
          </Dropdown>
        </Form.Item>
      )}

      {type === distributionTypeOptions?.[0].value && (
        <Form.Item label="Акция" name="action" rules={[{ required: true, message: 'Пожалуйста, выберите акцию' }]}>
          <Dropdown overlay={menu} trigger={['click']} disabled={!!isEdit}>
            <a onClick={(e) => e.preventDefault()} style={getDropdownStyles(!!isEdit)}>
              {selectedAction?.id ? selectedAction?.title : '--- Нажмите для выбора ---'}
            </a>
          </Dropdown>
        </Form.Item>
      )}

      <Form.Item label="Заголовок PUSH-уведомления" name="title" rules={[{ required: true }, { min: 3, max: 32 }]}>
        {/* <Input.TextArea
          autoSize
          placeholder="Введите заголовок PUSH-уведомления"
        /> */}
        <Input placeholder="Введите заголовок PUSH-уведомления" />
      </Form.Item>

      <Form.Item label="Текст PUSH-уведомления" name="text" rules={[{ required: true }, { min: 5, max: 120 }]}>
        <Input.TextArea autoSize placeholder="Введите текст PUSH-уведомления" />
      </Form.Item>

      {type !== DISTRIBUTION_BIRTHDAY_TYPE && (
        <Form.Item
          name="dateToSend"
          label="Время отправки"
          rules={[
            {
              type: 'object' as const,
              required: true,
              message: 'Пожалуйста, Выберите время',
            },
            () => ({
              validator: (_, value = {}) => {
                if (!!value && dayjs(value).isBefore(dayjs(), 'minute')) {
                  return Promise.reject('Вы не можете выбрать время, которое уже прошло');
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <DatePicker
            disabled={!!isEdit && type !== distributionTypeOptions?.[1].value}
            showTime={{
              format: 'HH:mm',
              minuteStep: MINUTE_STEP,
              defaultValue: roundedDate(MINUTE_STEP * 60 * 1000),
            }}
            format="DD MMM YYYY HH:mm"
            disabledDate={(date) => disabledPastDates(date, dayjs())}
            disabledTime={(date) => disabledPastTimes(date, dayjs(), MINUTE_STEP * 60 * 1000)}
            showNow={false}
            showToday={false}
          />
        </Form.Item>
      )}

      <Form.Item name="audience" label="Группа пользователей">
        <Radio.Group
          disabled={
            (!!isEdit && type !== distributionTypeOptions?.[1].value) ||
            (!isEdit && (!!selectedAction || !!selectedRec))
          }
        >
          {audienceOptions.map(({ value, label }) => (
            <Radio.Button key={value} value={value}>
              {label}
            </Radio.Button>
          ))}
        </Radio.Group>
      </Form.Item>

      <Form.Item label="Города" name="cityIds">
        <Select
          disabled={
            (!!isEdit && type !== distributionTypeOptions?.[1].value) ||
            (!isEdit && (!!selectedAction || !!selectedRec))
          }
          showSearch={false}
          placeholder="Выберите города"
          options={cities}
          loading={citiesLoading}
          mode="multiple"
        />
      </Form.Item>

      {radioField === AUDIENCE_AUTHORIZED_TYPE && (
        <>
          <Form.Item name="gender" label="Пол">
            <Select
              placeholder="Выберите пол"
              allowClear={true}
              options={genderOptions}
              disabled={
                (!!isEdit && type !== distributionTypeOptions?.[1].value) ||
                (!isEdit && (!!selectedAction || !!selectedRec))
              }
            />
          </Form.Item>
          <Form.Item name="age" label={`Возраст ${age?.join(' - ')}`}>
            <Slider
              disabled={
                (!!isEdit && type !== distributionTypeOptions?.[1].value) ||
                (!isEdit && (!!selectedAction || !!selectedRec))
              }
              min={MIN_AGE}
              max={MAX_AGE}
              range={true}
              tooltip={{ open: false }}
            />
          </Form.Item>
        </>
      )}
    </Form>
  );
};
